import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { taskNotifI18n } from './i18n';
import CreateEditTaskNotificationsModal from './CreateEditTaskNotificationsModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import {
  createTaskNotification,
  updateTaskNotification,
  deleteTaskNotification,
  getTaskNotificationsPaged,
} from '../../services/taskNotificationsService';
import CardStats from '../../components/CardStats/CardStats';
import './tasknotifications.scss';
import usePermissionsMessaging from 'permissions/modules/messaging/usePermissionsMessaging';

const TasksNotifications = ({ DesignSystemComponents }) => {
  const {
    Button,
    Card,
    Table,
    Badge,
    IconButton,
    Input,
    SvgIcon,
    Modal,
    Dropdown,
    ComboBox,
    Switch,
  } = DesignSystemComponents;
  const navigate = useNavigate();

  const { t } = useScopedTranslation(taskNotifI18n);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const { canCreateTask, canEditTask, canDeleteTask } =
    usePermissionsMessaging();

  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const getStatusLabel = (active) =>
    active ? t('tn_status_active') : t('tn_status_inactive');

  const mapSortKeyToBackend = useCallback((key) => {
    switch (key) {
      case 'codigo':
        return 'taskName';
      case 'plantilla':
        return 'templateName';
      case 'tipo':
        return 'messageType';
      case 'enviarInicio':
        return 'sendOnStart';
      case 'enviarFinal':
        return 'sendOnEnd';
      case 'grupo':
        return 'businessArea';
      case 'estado':
        return 'active';
      default:
        return null;
    }
  }, []);

  const backendSortKey = useMemo(
    () => mapSortKeyToBackend(sortKey),
    [sortKey, mapSortKeyToBackend]
  );

  const backendSortDesc = useMemo(() => {
    if (sortDirection === null || sortDirection === undefined) return null;
    return sortDirection === 'desc';
  }, [sortDirection]);

  const loadData = useCallback(async () => {
    try {
      const res = await getTaskNotificationsPaged(
        page + 1,
        pageSize,
        backendSortKey,
        backendSortDesc
      );

      const mapped = res.items.map((x) => ({
        id: x.id,
        taskConfiguratorId: x.taskConfiguratorId,
        businessAreaId: x.businessAreaId,
        templateId: x.templateId,
        sendMethodId: x.sendMethodId,
        codigo: x.taskName,
        descripcion: x.description ?? '',
        plantilla: x.templateName ?? '-',
        tipo: x.messageType ?? '-',
        enviarInicio: x.sendOnStart ?? false,
        enviarFinal: x.sendOnEnd ?? false,
        grupo: x.businessArea,
        active: x.active,
        sendOnStart: x.sendOnStart,
        sendOnEnd: x.sendOnEnd,
      }));

      setData(mapped);
      setTotalItems(res.totalCount);
    } catch (error) {
      console.error('Error loading configurations:', error);
    }
  }, [page, pageSize, backendSortKey, backendSortDesc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalRegistros = data.length;
  const totalActivos = data.filter((item) => item.active).length;
  const totalInactivos = data.filter((item) => !item.active).length;

  const confirmDelete = async () => {
    if (!toDelete) return;

    try {
      await deleteTaskNotification(toDelete.id);
      await loadData();
    } catch (error) {
      console.error('Error deleting configuration:', error);
    }

    setIsDeleteOpen(false);
    setToDelete(null);
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  return (
    <div>
      <div className='d-flex justify-content-between mb-4'>
        <div>
          <h2 className=' fw-semibold'>{t('tn_pageTitle')}</h2>
          <h4 className=' fw-normal'>{t('tn_pageSubtitle')}</h4>
        </div>

        <Button
          label={t('tn_newTask')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => {
            setEditingItem(null);
            setShowCreateEdit(true);
          }}
          disabled={!canCreateTask}
        />
      </div>

      <div className='row mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('tn_statTotal')}
            count={totalRegistros}
            subtitle={t('tn_statTotalSub')}
            icon='clipboard'
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('tn_statActive')}
            count={totalActivos}
            subtitle={t('tn_statActiveSub')}
            icon='checkCircle'
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('tn_statInactive')}
            count={totalInactivos}
            subtitle={t('tn_statInactiveSub')}
            icon='alertCircle'
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        <div className='mb-3'>
          <h3 className='h5 mb-1'>{t('tn_tableTitle')}</h3>
          <p className='caption font-light mb-0'>{t('tn_tableSubtitle')}</p>
        </div>

        <div className='table-container table-tasknotification'>
          <Table
            columns={[
              {
                accessor: 'codigo',
                label: t('tn_col_task'),
                sort: true,
                render: (row) => (
                  <div>
                    <p className='fw-semibold  mb-0'>{row.codigo}</p>
                    <p
                      className='caption font-light mb-0 text-truncate'
                      style={{
                        maxWidth: '240px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.descripcion}
                    </p>
                  </div>
                ),
              },
              {
                accessor: 'plantilla',
                label: t('tn_col_template'),
                sort: true,
                render: (row) => (
                  <p className='caption mb-0'>{row.plantilla}</p>
                ),
              },
              {
                accessor: 'tipo',
                label: t('tn_col_type'),
                sort: true,
                render: (row) => (
                  <Badge
                    text={row.tipo}
                    color={
                      row.tipo === 'SMS'
                        ? 'warning'
                        : row.tipo === 'WhatsApp'
                          ? 'success'
                          : 'primary'
                    }
                    size='sm'
                  />
                ),
              },
              {
                accessor: 'enviarInicio',
                label: t('tn_col_sendOnStart'),
                sort: true,
                render: (row) => (
                  <div
                    className='rounded-circle d-flex align-items-center justify-content-center'
                    style={{
                      width: 24,
                      height: 24,
                      backgroundColor: row.enviarInicio
                        ? 'var(--success-100)'
                        : 'var(--gray-100)',
                    }}
                  >
                    {row.enviarInicio && SvgIcon && (
                      <SvgIcon
                        name='checkCircle'
                        size='14px'
                        className='text-success'
                      />
                    )}
                  </div>
                ),
              },
              {
                accessor: 'enviarFinal',
                label: t('tn_col_sendOnEnd'),
                sort: true,
                render: (row) => (
                  <div
                    className='rounded-circle d-flex align-items-center justify-content-center'
                    style={{
                      width: 24,
                      height: 24,
                      backgroundColor: row.enviarFinal
                        ? 'var(--success-100)'
                        : 'var(--gray-100)',
                    }}
                  >
                    {row.enviarFinal && SvgIcon && (
                      <SvgIcon
                        name='checkCircle'
                        size='14px'
                        className='text-success'
                      />
                    )}
                  </div>
                ),
              },
              {
                accessor: 'grupo',
                label: t('tn_col_group'),
                sort: true,
                render: (row) => <p className='caption mb-0'>{row.grupo}</p>,
              },
              {
                accessor: 'estado',
                label: t('tn_col_status'),
                sort: true,
                render: (row) => (
                  <Badge
                    text={getStatusLabel(row.active)}
                    color={row.active ? 'success' : 'error'}
                    size='sm'
                  />
                ),
              },
              {
                label: t('tn_col_actions'),
                render: (row) => (
                  <div className='d-flex justify-content-end gap-2'>
                    <IconButton
                      ariaLabel={t('tn_aria_edit')}
                      icon='pencil'
                      size='md'
                      onClick={() => {
                        setEditingItem(row);
                        setShowCreateEdit(true);
                      }}
                      iconStroke='var(--primary-500)'
                      disabled={!canEditTask}
                    />
                    <IconButton
                      ariaLabel={t('tn_aria_delete')}
                      icon='trash'
                      size='md'
                      onClick={() => {
                        setToDelete(row);
                        setIsDeleteOpen(true);
                      }}
                      iconStroke='var(--error-500)'
                      disabled={!canDeleteTask}
                    />
                  </div>
                ),
              },
            ]}
            data={data}
            paginator
            paginationMode='server'
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={totalItems}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </div>
      </Card>

      <CreateEditTaskNotificationsModal
        DesignSystemComponents={{
          Button,
          Card,
          Input,
          Dropdown,
          Switch,
          ComboBox,
        }}
        show={showCreateEdit}
        mode={editingItem ? 'edit' : 'create'}
        initialData={editingItem}
        onClose={() => {
          setShowCreateEdit(false);
          setEditingItem(null);
        }}
        onSave={async (payload) => {
          try {
            if (!editingItem) {
              await createTaskNotification(payload);
            } else {
              await updateTaskNotification(payload);
            }
            await loadData();
          } catch (err) {
            console.error('Error saving configuration:', err);
          }

          setShowCreateEdit(false);
          setEditingItem(null);
        }}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('tn_delete_title')}
        message={
          toDelete
            ? t('tn_delete_message', { name: toDelete.codigo })
            : t('tn_delete_fallback')
        }
        confirmLabel={t('tn_delete_confirm')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
};

export default TasksNotifications;
